import { defineStore } from "pinia";
import {
  AnyLocation,
  DatasetRecord,
  EnergyRatingReport,
  GDT,
  IDisplaySection,
  ILocation,
  RiskLookupType,
} from "@/types";
import {
  useAuth,
  useGammaData,
  useGammaWebServices,
  useLayout,
  useMap,
  useOrgSettings,
} from "@/services";
import { get, isEqual } from "lodash-es";
import { formatAddress } from "@/util/helpers";
import GeoJSON from "geojson";
import { updateUrlQueryParams } from "@/router";
import { createLocationInstance } from "@/util/locationFactory";
import useRiskGeometry from "@/composeable/risk-geometry";
import { GammaAccuracy } from "@/types/location/location-accuracy";
import { DatasetRecordIdentifier } from "@gamma/types.data";

interface LocationState {
  location: null | AnyLocation;
  savedLocations: ILocation[];
  energyRating?: EnergyRatingReport;
  isEnergyRatingLoading: boolean;
}
export const useLocation = defineStore("location", {
  state: (): LocationState => ({
    location: null,
    savedLocations: [],
    energyRating: undefined,
    isEnergyRatingLoading: false,
  }),
  actions: {
    updateLocation(data: ILocation | null, zoomTo = false) {
      useMap().markerPosition = null;
      const map = useMap();
      if ((data?.type !== "Geometry" && map.isDrawingRiskGeom) || !data) {
        this.location = null;
        useRiskGeometry().endDraw();
      }
      if (data?.type === "Geometry") {
        useRiskGeometry().set(data, zoomTo);
      }

      if (data?.query) {
        return useGammaData().fetchDatasetRecord(
          data as { query: Record<string, any>; lcId: string }
        );
      } else {
        useLayout().isLeftSidebarOpen = !!data;
        if (!isEqual(data, this.location?.urlParams)) {
          this.location = data ? createLocationInstance(data) : null;
          if (data) {
            this.setUrlAddress(
              (get(this, "location.urlParams") as
                | ILocation
                | DatasetRecordIdentifier) || null
            );
          }
          if (this.location && this.addressLinkRequest) {
            useGammaWebServices()
              .fetchRiskLookups(
                this.location as AnyLocation,
                useOrgSettings().riskLookups
              )
              .then(() => {
                if (this.location) {
                  useGammaWebServices().logLocation(
                    this.location as AnyLocation
                  );
                }
              });

            const risksChart = useOrgSettings().riskLookups.find(
              (x) => x.type === RiskLookupType.FloodClimateChangeChart
            );

            if (risksChart) {
              useGammaWebServices().loadChartData(
                this.location as AnyLocation,
                risksChart
              );
            }
          }
        }
      }
      return Promise.resolve();
    },
    async loadEnergyRating(location: AnyLocation) {
      this.energyRating = undefined;
      const epcRating = useOrgSettings().selectedBusinessLine?.epcRating;
      if (!epcRating) return;

      this.isEnergyRatingLoading = true;
      try {
        this.energyRating =
          await useGammaWebServices().fetchLocationEnergyRating(
            location,
            epcRating.url,
            epcRating.licenceKey
          );
      } catch (e) {
        if (e.response.data.StatusCode === 404) {
          this.energyRating = {
            current_energy_rating: "Unknown",
            potential_energy_rating: "Unknown",
            current_energy_efficiency: 0,
            potential_energy_efficiency: 0,
            inspection_date: "",
          };
        } else {
          throw e;
        }
      } finally {
        this.isEnergyRatingLoading = false;
      }
    },
    async reloadLocation(): Promise<void> {
      if (this.location) {
        await this.updateLocation(this.location as ILocation);
      }
    },
    async setUrlAddress(data: ILocation | GDT.DatasetRecordIdentifier | null) {
      if (useLayout().page === "map") {
        await updateUrlQueryParams("address", data, true);
      }
    },
    save(location: ILocation) {
      this.savedLocations.push(location);
      this.saveLocationsToLocalStorage();
    },

    remove(location: ILocation) {
      const index = this.savedLocations.findIndex((x) => isEqual(location, x));

      if (index === -1) return;

      this.savedLocations.splice(index, 1);
      this.saveLocationsToLocalStorage();
    },

    loadLocationsFromLocalStorage() {
      const userId = useAuth().userInfo?.id;
      if (!userId) {
        this.savedLocations = [];
        return;
      }
      const locationsString = localStorage.getItem(userId);

      const locations: ILocation[] = locationsString
        ? JSON.parse(locationsString)
        : [];
      this.savedLocations = locations.map((x) => {
        if ((x.type === "Claim" || x.type === "Policy") && x.record) {
          const { data, columnDefinitions, props } = x.record;
          return {
            ...x,
            record: new DatasetRecord(data, columnDefinitions, props),
          } as ILocation;
        }

        return x;
      });
    },
    saveLocationsToLocalStorage() {
      const userId = useAuth().userInfo?.id;
      if (!userId) {
        return;
      }

      localStorage.setItem(userId, JSON.stringify(this.savedLocations));
    },
  },
  getters: {
    displaySections(): IDisplaySection[] {
      return this.location?.displaySections || [];
    },
    coordinates(): GeoJSON.Position {
      return this.location?.geometry.coordinates.map((n) => +n) || [];
    },
    addressLinkRequest(): any | undefined {
      return this.location?.addressLinkRequest;
    },
    address(): any {
      return this.location?.address;
    },
    formattedAddress(): string {
      return formatAddress(this.address);
    },
    record(): DatasetRecord | null {
      return (this.location?.record as DatasetRecord) || null;
    },
    locationType(): string {
      return this.location?.type || "";
    },
    isSaved(): boolean {
      return this.savedLocations.some((x) => isEqual(this.location?._data, x));
    },
    precisionAccuracy(): GammaAccuracy | undefined {
      return this.location?.precisionAccuracy;
    },
  },
});
